import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ComunidadLaunchReadState,
  ComunidadLaunchState,
  dismissComunidadLaunch,
  getComunidadLaunchState,
  markComunidadVisited,
  snoozeComunidadLaunch,
} from '@/hooks/api/comunidadLaunch';
import { isImpersonated } from '@/hooks/api/auth/tokenManager';
import { COMUNIDAD_LAUNCH_ENDS_AT_MS } from '@/utils/comunidadAccess';

/** Debe coincidir con SNOOZE_MS del BE (ComunidadLaunchService). */
const SNOOZE_MS = 3 * 60 * 60 * 1000;

const FRESH_STATE: ComunidadLaunchState = { dismissed: false, snoozedUntil: null, visited: false };

/** Clave de localStorage del camino impersonado — por psicóloga, así cada cuenta que soporte
 *  mira tiene su propio estado y nada de esto toca a la psicóloga real. */
const impersonatedKey = (psychologistId: string) => `psi-comunidad-launch-impersonated-${psychologistId}`;

function readImpersonated(psychologistId: string): ComunidadLaunchState {
  try {
    const raw = localStorage.getItem(impersonatedKey(psychologistId));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ComunidadLaunchState>;
      return {
        dismissed: !!parsed.dismissed,
        snoozedUntil: typeof parsed.snoozedUntil === 'string' ? parsed.snoozedUntil : null,
        visited: !!parsed.visited,
      };
    }
  } catch {
    /* localStorage bloqueado o JSON roto: se arranca de cero */
  }
  return FRESH_STATE;
}

function writeImpersonated(psychologistId: string, state: ComunidadLaunchState) {
  try {
    const { dismissed, snoozedUntil, visited } = state;
    localStorage.setItem(impersonatedKey(psychologistId), JSON.stringify({ dismissed, snoozedUntil, visited }));
  } catch {
    /* sin storage, el aviso solo vuelve a salir en la próxima carga */
  }
}

/**
 * Estado del pop-up de lanzamiento de Comunidad y del badge "Nuevo" de la pestaña. Dos caminos
 * separados, para que lo que haga soporte nunca le apague el aviso a la psicóloga real:
 *
 * - Psicóloga real: lee el estado del backend (`eligible` incluye la ventana de fechas y que
 *   estuviera dada de alta antes del lanzamiento) y persiste dismiss / snooze / visited allá.
 * - Sesión impersonada: el estado vive solo en el `localStorage` de ESE navegador, por
 *   psicóloga. Cerrar el pop-up impersonando la oculta para esa cuenta en esa compu, y nada
 *   más. No lee ni escribe el backend; la elegibilidad es solo la ventana de fechas.
 *
 * Fail-closed: si la lectura del backend falla, no se muestra ni el pop-up ni el badge.
 */
export function useComunidadLaunch(enabled: boolean, psychologistId: string | undefined) {
  const impersonated = isImpersonated();
  const [state, setState] = useState<ComunidadLaunchReadState | null>(null);
  // Espejo síncrono del estado: las acciones calculan el siguiente estado desde acá para poder
  // persistirlo sin disparar efectos secundarios dentro de un updater de setState.
  const stateRef = useRef<ComunidadLaunchReadState | null>(null);
  const visitedSentRef = useRef(false);

  const commit = useCallback((next: ComunidadLaunchReadState | null) => {
    stateRef.current = next;
    setState(next);
  }, []);

  useEffect(() => {
    if (!enabled || !psychologistId) return;
    visitedSentRef.current = false;

    if (impersonated) {
      commit({ ...readImpersonated(psychologistId), eligible: Date.now() < COMUNIDAD_LAUNCH_ENDS_AT_MS });
      return;
    }

    let cancelled = false;
    getComunidadLaunchState()
      .then((s) => { if (!cancelled) commit(s); })
      .catch(() => {
        if (!cancelled) commit({ dismissed: true, snoozedUntil: null, visited: true, eligible: false });
      });
    return () => { cancelled = true; };
  }, [enabled, impersonated, psychologistId, commit]);

  const apply = useCallback(
    (patch: Partial<ComunidadLaunchState>, remote: () => Promise<unknown>) => {
      const base = stateRef.current;
      if (!base) return;
      const next = { ...base, ...patch };
      commit(next);
      if (impersonated) {
        if (psychologistId) writeImpersonated(psychologistId, next);
      } else {
        // Optimista: la pantalla ya cambió. Si el POST falla, el aviso vuelve a salir en la
        // próxima carga; no se molesta a la psicóloga con un error por esto.
        remote().catch(() => { /* noop */ });
      }
    },
    [commit, impersonated, psychologistId],
  );

  const dismiss = useCallback(
    () => apply({ dismissed: true, snoozedUntil: null }, dismissComunidadLaunch),
    [apply],
  );

  const snooze = useCallback(
    () => apply({ snoozedUntil: new Date(Date.now() + SNOOZE_MS).toISOString() }, snoozeComunidadLaunch),
    [apply],
  );

  const markVisited = useCallback(() => {
    if (visitedSentRef.current) return;
    visitedSentRef.current = true;
    apply({ visited: true }, markComunidadVisited);
  }, [apply]);

  const snoozed = !!state?.snoozedUntil && new Date(state.snoozedUntil) > new Date();

  return {
    showModal: enabled && !!state && state.eligible && !state.dismissed && !snoozed,
    showBadge: enabled && !!state && state.eligible && !state.visited,
    dismiss,
    snooze,
    markVisited,
  };
}
